import type {
  InventoryCompletenessState,
  InventoryYearTotals,
  SectorId,
  SectorYearValue,
} from './types';

function computeCompletenessState(
  yearValues: SectorYearValue[],
  wastewaterRequired = true
): {
  state: InventoryCompletenessState;
  missingSectors: SectorId[];
  estimatedSectors: SectorId[];
  wastewaterIncludedInTotal: boolean;
} {
  const ww = yearValues.find((s) => s.sectorId === 'wastewater');
  const missingSectors = yearValues
    .filter((s) => s.status === 'unavailable')
    .map((s) => s.sectorId);
  const estimatedSectors = yearValues
    .filter((s) => s.status === 'estimated' || s.status === 'provisional')
    .map((s) => s.sectorId);

  const wastewaterUnavailable =
    wastewaterRequired && (!ww || ww.status === 'unavailable' || ww.emissionsMt == null);

  if (wastewaterUnavailable) {
    return {
      state: 'partial',
      missingSectors,
      estimatedSectors,
      wastewaterIncludedInTotal: false,
    };
  }

  const hasEstimated = estimatedSectors.length > 0;
  return {
    state: hasEstimated ? 'complete_with_estimates' : 'complete',
    missingSectors,
    estimatedSectors,
    wastewaterIncludedInTotal: ww != null && ww.emissionsMt != null,
  };
}

export function sumSectorValuesForYear(
  sectorValues: SectorYearValue[],
  year: number,
  options?: { wastewaterRequired?: boolean }
): InventoryYearTotals {
  const yearValues = sectorValues.filter((s) => s.year === year);
  const { state, missingSectors, estimatedSectors, wastewaterIncludedInTotal } =
    computeCompletenessState(yearValues, options?.wastewaterRequired ?? true);

  const numeric = yearValues
    .filter((s) => s.emissionsMt != null && Number.isFinite(s.emissionsMt))
    .map((s) => s.emissionsMt as number);

  const totalMt = numeric.length > 0 ? numeric.reduce((a, b) => a + b, 0) : null;

  return {
    year,
    sectorValues: yearValues,
    totalMt: totalMt != null ? Math.round(totalMt) : null,
    completenessState: state,
    completeInventory: state === 'complete',
    missingSectors,
    estimatedSectors,
    wastewaterIncludedInTotal,
  };
}

/** Headline total for a year — includes estimated wastewater; excludes unavailable (partial). */
export function headlineTotalFromSectors(
  sectorValues: SectorYearValue[],
  year: number
): { totalMt: number | null; completenessState: InventoryCompletenessState } {
  const totals = sumSectorValuesForYear(sectorValues, year);
  return { totalMt: totals.totalMt, completenessState: totals.completenessState };
}
